import { useRef, useCallback } from 'react';
import type { Language, RichContent } from '@pullman/shared';
import { MOCK_PROPERTIES } from '@/data/mockProperties';

interface StreamCallbacks {
  onToken: (token: string) => void;
  onRichContent: (content: RichContent) => void;
  onComplete: () => void;
  onError: (error: string) => void;
}

const MOCK_RESPONSES: Record<string, { text: string; richContent?: RichContent }> = {
  explore: {
    text: "I'd love to help you explore our apartments! Pullman Residences Yerevan offers 391 units across 4 blocks, with 10 distinct apartment types ranging from 62 sqm studios to 271 sqm premium residences.\n\nHere's one of our most popular options - the Type 1 two-bedroom apartment. It offers a wonderful balance of space and value:",
    richContent: {
      type: 'property-card',
      data: { property: MOCK_PROPERTIES[0] },
    },
  },
  pricing: {
    text: "Great question! Pullman Residences offers flexible payment plans designed to make your purchase comfortable.\n\nOur payment structure is:\n- 30% down payment at booking\n- 2% monthly installments over 30 months\n- 10% final payment at handover (end of 2027)\n\nLet me show you a breakdown for our Type 1 apartment:",
    richContent: {
      type: 'payment-calculator',
      data: { property: MOCK_PROPERTIES[0] },
    },
  },
  schedule: {
    text: "I'd be happy to arrange a meeting for you! You can visit our showroom at Aghbyur Serob 50, Yerevan, or we can set up a video call if you're abroad.\n\nPlease select a convenient date and time:",
    richContent: {
      type: 'meeting-scheduler',
      data: {},
    },
  },
  about: {
    text: "Pullman Residences Yerevan is the first-ever Pullman branded residences in Armenia, a partnership between Technotun LLC and Accor Group - the world's leading hospitality company.\n\nThe development spans over 20,000 sqm with 391 apartments across 4 blocks, featuring 16,000 sqm of landscaped green spaces. Every apartment comes with stunning views of the Armenian Highlands.\n\nAs a homeowner, you'll receive automatic Platinum status in Accor's Live Limitless loyalty program, giving you access to 5,600+ hotels worldwide with complimentary upgrades, executive lounge access, and more.\n\nWould you like to explore our apartment types or learn about our amenities?",
  },
  default: {
    text: "Thank you for your interest in Pullman Residences Yerevan! I'm here to help you find your perfect home.\n\nI can help you with:\n- Exploring our 10 apartment types (62-271 sqm)\n- Understanding pricing and payment plans\n- Scheduling a visit or virtual consultation\n- Learning about our world-class amenities\n\nWhat would you like to know more about?",
  },
};

function classifyMessage(message: string): string {
  const lower = message.toLowerCase();
  if (lower.includes('apartment') || lower.includes('explore') || lower.includes('type') || lower.includes('bedroom') || lower.includes('unit')) {
    return 'explore';
  }
  if (lower.includes('price') || lower.includes('pricing') || lower.includes('payment') || lower.includes('cost') || lower.includes('plan') || lower.includes('installment')) {
    return 'pricing';
  }
  if (lower.includes('meet') || lower.includes('schedule') || lower.includes('visit') || lower.includes('tour') || lower.includes('call')) {
    return 'schedule';
  }
  if (lower.includes('pullman') || lower.includes('about') || lower.includes('accor') || lower.includes('tell me') || lower.includes('brand')) {
    return 'about';
  }
  return 'default';
}

export function useChatStream() {
  const abortRef = useRef<AbortController | null>(null);

  const sendStream = useCallback(
    async (
      sessionId: string,
      message: string,
      language: Language,
      callbacks: StreamCallbacks,
    ) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      // Try real backend first
      try {
        const apiBase = import.meta.env.VITE_API_URL || '';
        const res = await fetch(`${apiBase}/api/chat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId, message, language }),
          signal: controller.signal,
        });

        if (!res.ok || !res.body) throw new Error('Backend unavailable');

        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() ?? '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            const raw = line.slice(6).trim();
            if (raw === '[DONE]') {
              callbacks.onComplete();
              return;
            }
            try {
              const event = JSON.parse(raw) as { type: string; data: string | RichContent };
              if (event.type === 'text_delta') {
                callbacks.onToken(event.data as string);
              } else if (event.type === 'rich_content') {
                callbacks.onRichContent(event.data as RichContent);
              } else if (event.type === 'message_stop') {
                callbacks.onComplete();
                return;
              }
            } catch {
              // skip malformed
            }
          }
        }
        callbacks.onComplete();
        return;
      } catch {
        // Fall through to mock mode
      }

      // MOCK MODE: simulate streaming
      const classification = classifyMessage(message);
      const mockResp = MOCK_RESPONSES[classification] ?? MOCK_RESPONSES.default;
      const words = mockResp.text.split(' ');

      for (let i = 0; i < words.length; i++) {
        if (controller.signal.aborted) return;
        await new Promise((r) => setTimeout(r, 30 + Math.random() * 40));
        callbacks.onToken((i === 0 ? '' : ' ') + words[i]);
      }

      if (mockResp.richContent && !controller.signal.aborted) {
        await new Promise((r) => setTimeout(r, 300));
        callbacks.onRichContent(mockResp.richContent);
      }

      if (!controller.signal.aborted) {
        callbacks.onComplete();
      }
    },
    [],
  );

  const cancel = useCallback(() => {
    abortRef.current?.abort();
  }, []);

  return { sendStream, cancel };
}
